/**
 * 入会前的准备页：确认设备、决定进去时开不开麦克风和摄像头。
 *
 * 老系统是「点进去就直接进课堂」：设备有没有问题、麦克风是不是开着，
 * 都得进去之后才知道。最常见的两种事故正好在这里发生 ——
 * 进去才发现摄像头打不开，和进去就把没准备好的画面推给全班。
 *
 * 所以这一步做两件事：把设备验一遍，把「进去是什么状态」交给用户自己定。
 */

import { useEffect, useRef, useState } from 'react'

import { getPreferred, listDevices, setPreferred, type DeviceList } from '../lib/devices'
import { mediaBlockedReason } from '../lib/media'

export interface PrejoinChoice {
  micOn: boolean
  camOn: boolean
}

interface Props {
  roomName: string
  /** 房间开了「成员入会时静音」时，麦克风默认关掉（主持人由调用方决定是否传 false）。 */
  muteOnJoin?: boolean
  nick: string
  onEnter: (c: PrejoinChoice) => void
  onCancel: () => void
  busy?: boolean
}

export function Prejoin({ roomName, muteOnJoin = false, nick, onEnter, onCancel, busy }: Props) {
  const [list, setList] = useState<DeviceList>({ cameras: [], mics: [], speakers: [] })
  const [sel, setSel] = useState(getPreferred())
  // 默认开麦，除非房间开了入会静音 —— 大班课几十人陆续进场全开麦时，
  // 前十分钟全是杂音，老师第一件事永远是「全体静音」。
  const [micOn, setMicOn] = useState(!muteOnJoin)
  const [camOn, setCamOn] = useState(false)
  const [err, setErr] = useState(mediaBlockedReason() ?? '')
  // 浏览器根本没开放媒体接口（http 加 IP 访问时必然如此，局域网里用地址访问会议节点就是这种）。
  // 这时开关必须显示「不可用」并按关着入会：以前按钮灰着却写「麦克风已开」，
  // 还把 micOn: true 带进教室再去开一个不存在的设备 —— 用户以为开着，别人却听不见。
  // 只管「接口不存在」这一种；设备打不开之类的错误保持原样，进教室时还有机会重试成功。
  const blocked = mediaBlockedReason() !== null
  const [level, setLevel] = useState(0)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (err) return
    let alive = true
    listDevices()
      .then((d) => alive && setList(d))
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [err])

  // 预览流。摄像头关着时只取音频，这样「摄像头关闭」是真的关着，
  // 而不是画面藏起来、指示灯还亮着。
  useEffect(() => {
    if (err || (!micOn && !camOn)) {
      setLevel(0)
      return
    }
    let alive = true
    let stream: MediaStream | null = null
    let ctx: AudioContext | null = null
    let raf = 0

    ;(async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: camOn ? (sel.camera ? { deviceId: { ideal: sel.camera } } : true) : false,
          audio: micOn ? (sel.mic ? { deviceId: { ideal: sel.mic } } : true) : false,
        })
        if (!alive) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        if (videoRef.current && camOn) {
          videoRef.current.srcObject = stream
          videoRef.current.play().catch(() => {})
        }
        if (micOn) {
          const Ctor =
            window.AudioContext ||
            (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
          ctx = new Ctor()
          const an = ctx.createAnalyser()
          an.fftSize = 512
          ctx.createMediaStreamSource(stream).connect(an)
          const buf = new Uint8Array(an.frequencyBinCount)
          const tick = () => {
            an.getByteFrequencyData(buf)
            let peak = 0
            for (const v of buf) if (v > peak) peak = v
            setLevel(peak)
            raf = requestAnimationFrame(tick)
          }
          tick()
        }
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : '打不开所选设备')
      }
    })()

    return () => {
      alive = false
      cancelAnimationFrame(raf)
      void ctx?.close()
      stream?.getTracks().forEach((t) => t.stop())
    }
  }, [micOn, camOn, sel.camera, sel.mic, err])

  const choose = (kind: 'camera' | 'mic', id: string) => {
    setPreferred(kind, id)
    setSel((s) => ({ ...s, [kind]: id }))
  }

  return (
    <div className="prejoin">
      <div className="pj-card">
        <h2>{roomName || '准备进入教室'}</h2>
        <p className="pj-who">以 {nick} 的身份加入</p>

        <div className="pj-preview">
          {camOn && !err ? (
            <video ref={videoRef} autoPlay playsInline muted />
          ) : (
            <div className="pj-off">{err ? err : '摄像头已关闭'}</div>
          )}
        </div>

        <div className="pj-toggles">
          <button className={micOn && !blocked ? 'on' : ''} onClick={() => setMicOn((v) => !v)} disabled={!!err}>
            {blocked ? '🔇 麦克风不可用' : micOn ? '🎙️ 麦克风已开' : '🔇 麦克风已关'}
          </button>
          <button className={camOn && !blocked ? 'on' : ''} onClick={() => setCamOn((v) => !v)} disabled={!!err}>
            {blocked ? '📷 摄像头不可用' : camOn ? '📹 摄像头已开' : '📷 摄像头已关'}
          </button>
        </div>

        {/* 音量条是「麦克风到底通没通」唯一看得见的证据。 */}
        {micOn && !err && (
          <div className="pj-meter" title="说句话，这条会跟着动">
            <span style={{ width: `${Math.min(100, (level / 180) * 100)}%` }} />
          </div>
        )}

        <div className="pj-devices">
          <label>
            摄像头
            <select value={sel.camera} onChange={(e) => choose('camera', e.target.value)}>
              <option value="">跟随系统默认</option>
              {list.cameras.map((d) => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </select>
          </label>
          <label>
            麦克风
            <select value={sel.mic} onChange={(e) => choose('mic', e.target.value)}>
              <option value="">跟随系统默认</option>
              {list.mics.map((d) => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="pj-actions">
          <button onClick={onCancel}>返回</button>
          <button className="primary" onClick={() => onEnter({ micOn: micOn && !blocked, camOn: camOn && !blocked })} disabled={busy}>
            {busy ? '正在进入…' : '进入教室'}
          </button>
        </div>
      </div>
    </div>
  )
}
